import React, { useState, useEffect } from 'react';
import { FormikProps, Form, Field, useFormikContext } from 'formik';

import { Flex, Box } from 'rebass';
import { FormFields } from './validation';

import {
  FormSection,
  TextareaField,
  TextField,
  DateField,
  RadioGroupField,
  Radio,
  DropDownField,
  LegendaryLabel,
} from '../../components/styled/forms';
import { AnnounceChange } from '../../components/styled/forms/AnnounceChange';
import {
  MobileColumnDesktopRowCell,
  MobileColumnDesktopRow,
} from '../../components/styled/layout';

export const ReferralForm: React.FC<{ formik: FormikProps<FormFields> }> = ({
  formik,
}) => {
  const { values, setFieldTouched, setFieldValue } = useFormikContext<
    FormFields
  >();

  const [showFullForm, setShowFullForm] = useState<boolean>(false);

  const showFullFormWhenChildInfoCompleted = () => {
    if (
      values.childFirstName &&
      values.childLastName &&
      values.childDateOfBirth &&
      values.sexOfChild
    ) {
      setShowFullForm(true);
    }
  };

  useEffect(showFullFormWhenChildInfoCompleted, [values]);

  const resetOtherSexOfChildWhenNotOther = () => {
    if (values.sexOfChild !== 'other') {
      setFieldTouched('otherSexOfChild', false);
      setFieldValue('otherSexOfChild', '');
    }
  };

  useEffect(
    resetOtherSexOfChildWhenNotOther,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.sexOfChild]
  );

  const resetPrimaryContactMailingAddressWhenSame = () => {
    if (values.primaryContactSameAddresses === false) {
      setFieldTouched('primaryContactMailingAddress', false);
      setFieldValue('primaryContactMailingAddress', '');
    }
  };

  useEffect(
    resetPrimaryContactMailingAddressWhenSame,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.primaryContactSameAddresses]
  );

  return (
    <Form
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MobileColumnDesktopRow>
        <MobileColumnDesktopRowCell>
          <Flex flexDirection={'column'}>
            <FormSection
              name={'Child'}
              data-testid="child-section"
              disabled={false}
              focused={true}
            >
              <TextField name="childFirstName" label="First name" />
              <TextField name="childLastName" label="Last name" />
              <DateField name="childDateOfBirth" label="Date of birth" />
              <Box>
                <RadioGroupField name="sexOfChild">
                  <LegendaryLabel>Sex of child</LegendaryLabel>
                  <Radio
                    value="female"
                    data-testid="sexOfChildIsFemale"
                    label="Female"
                  />
                  <Radio
                    value="male"
                    data-testid="sexOfChildIsMale"
                    label="Male"
                  />
                  <Radio
                    value="other"
                    data-testid="sexOfChildIsOther"
                    label="Other"
                  />
                </RadioGroupField>
              </Box>
              <AnnounceChange>
                {formik.values.sexOfChild === 'other' && (
                  <TextField
                    name="otherSexOfChild"
                    id="otherSexOfChild"
                    required={true}
                    label="Specify sex of child"
                  />
                )}
              </AnnounceChange>
            </FormSection>
            <FormSection
              name={'Primary Contact'}
              data-testid="primary-contact-section"
              disabled={!showFullForm}
            >
              <TextField name="primaryContactFirstName" label="First name" />
              <TextField name="primaryContactLastName" label="Last name" />

              <DropDownField
                name={'preferredContactMethod'}
                defaultValue={''}
                label={'Preferred contact method'}
                options={[
                  { label: '' },
                  { label: 'Phone', value: 'phone' },
                  { label: 'Email', value: 'email' },
                ]}
              />

              <TextField
                name={'primaryContactPhone'}
                label={'Phone'}
                required={formik.values.preferredContactMethod === 'phone'}
              />
              <TextField
                name={'primaryContactEmail'}
                label={'Email'}
                type="email"
                required={formik.values.preferredContactMethod === 'email'}
              />

              <TextareaField
                name={'primaryContactPhysicalAddress'}
                label={'Physical Address'}
                required={formik.values.primaryContactSameAddresses === false}
              />
              <Box>
                <label htmlFor="primaryContactSameAddresses">
                  <Field
                    name="primaryContactSameAddresses"
                    id="primaryContactSameAddresses"
                    type="checkbox"
                    aria-label="Physical address is same as mailing address"
                  />
                  Same as mailing address
                </label>
              </Box>
              <AnnounceChange>
                {formik.values.primaryContactSameAddresses === false && (
                  <TextareaField
                    name={'primaryContactMailingAddress'}
                    label={'Mailing Address'}
                    required={
                      formik.values.primaryContactSameAddresses === false
                    }
                  />
                )}
              </AnnounceChange>
            </FormSection>
          </Flex>
        </MobileColumnDesktopRowCell>
        <MobileColumnDesktopRowCell>
          <FormSection
            name={'Referral Details'}
            disabled={!showFullForm}
            data-testid="referral-details-section"
          >
            <DateField
              name="dateOfInitialReferral"
              label="Date of initial referral"
            />
            <TextField name={'referringPartyFirstName'} label={'First Name'} />
            <TextField name={'referringPartyLastName'} label={'Last Name'} />
            <TextareaField name={'referralReason'} label={'Referral reason'} />
          </FormSection>
        </MobileColumnDesktopRowCell>
      </MobileColumnDesktopRow>
    </Form>
  );
};
